import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE } from '../config/env';
import './login.css';

type AuthUser = {
  sub?: string;
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  picture?: string | null;
  role?: string;
  roles?: string[];
  platformAdmin?: boolean;
};

type StoredSession = {
  accessToken: string;
  refreshToken: string;
  user: AuthUser | null;
  expiresAt: number;
  remember: boolean;
};

type AuthContextValue = {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | null;
  user: AuthUser | undefined;
  getAccessTokenSilently: (_options?: Record<string, unknown>) => Promise<string>;
  loginWithRedirect: (options?: Record<string, unknown> & { appState?: { returnTo?: string } }) => Promise<void>;
  logout: (options?: { logoutParams?: { returnTo?: string } }) => void;
  loginWithPassword: (
    email: string,
    password: string,
    options?: { remember?: boolean; language?: LoginLanguage },
  ) => Promise<{ mustChangePassword?: boolean; isPlatformAdmin?: boolean; requiresTwoFactor?: boolean; challengeId?: string; method?: string; destination?: string; expiresAt?: string }>;
  verifyTwoFactor: (
    challengeId: string,
    code: string,
    options?: { remember?: boolean },
  ) => Promise<{ mustChangePassword: boolean; isPlatformAdmin: boolean }>;
};

const STORAGE_KEY = 'neoemr.staff.auth';
const CONNECTED_ACCOUNTS_KEY = 'neoemr.staff.connectedAccounts';
const LOGIN_PATH = '/login';
const DEFAULT_RETURN_TO = '/appointments';
const TOKEN_REFRESH_SKEW_MS = 45_000;
const LOGIN_LANGUAGE_QUERY_PARAM = 'language';

type LoginLanguage = 'en' | 'es';
type AuthLoginVariant = 'staff' | 'admin' | 'portal';
type AuthMode = 'login' | 'forgot' | 'reset';

type BaseLoginCopy = {
  contextLabel: string;
  panelKicker: string;
  headline: string;
  subheadline: string;
  emailLabel: string;
  passwordLabel: string;
  forgotPassword: string;
  rememberDevice: string;
  submit: string;
  submitting: string;
  supportCopy: string;
  privacyPolicy: string;
  securityProtocol: string;
  systemStatus: string;
  heroLead: string;
  heroMiddle: string;
  heroAccent: string;
  genericError: string;
};

type PasswordResetCopy = {
  emailPlaceholder: string;
  passwordPlaceholder: string;
  forgotHeadline: string;
  forgotSubheadline: string;
  forgotSubmit: string;
  forgotSubmitting: string;
  forgotSuccess: string;
  backToSignIn: string;
  resetHeadline: string;
  resetSubheadline: string;
  newPasswordLabel: string;
  confirmPasswordLabel: string;
  newPasswordPlaceholder: string;
  confirmPasswordPlaceholder: string;
  resetSubmit: string;
  resetSubmitting: string;
  resetSuccess: string;
  passwordGuidance: string;
  passwordMismatch: string;
  resetTokenMissing: string;
  resetTokenInvalid: string;
  samePasswordError: string;
  requestNewLink: string;
};

type LoginCopy = BaseLoginCopy & PasswordResetCopy;

const HOST_DEFAULT_LANGUAGE_MAP: Record<string, LoginLanguage> = {
  'app.elen1.com': 'en',
  'mi.elen1.com': 'es',
};

const LOGIN_COPY_BASE: Record<AuthLoginVariant, Record<LoginLanguage, BaseLoginCopy>> = {
  staff: {
    en: {
      contextLabel: 'Elen1 EMR',
      panelKicker: 'System verification',
      headline: 'Welcome Back',
      subheadline: 'Enter your credentials to access the workspace.',
      emailLabel: 'Email address',
      passwordLabel: 'Password',
      forgotPassword: 'Forgot password?',
      rememberDevice: 'Remember device for 30 days',
      submit: 'Sign in',
      submitting: 'Signing in...',
      supportCopy: 'Need access ? Contact your organization administrator.',
      privacyPolicy: 'Privacy policy',
      securityProtocol: 'Security protocol',
      systemStatus: 'ACTIVE',
      heroLead: 'A Modern',
      heroMiddle: 'Clinical Intelligence',
      heroAccent: 'Platform.',
      genericError: 'Unable to sign in.',
    },
    es: {
      contextLabel: 'Elen1 EMR',
      panelKicker: 'Verificacion del sistema',
      headline: 'Bienvenido de nuevo',
      subheadline: 'Ingrese sus credenciales para acceder al espacio de trabajo.',
      emailLabel: 'Correo electronico',
      passwordLabel: 'Contrasena',
      forgotPassword: 'Olvido su contrasena?',
      rememberDevice: 'Recordar este dispositivo durante 30 dias',
      submit: 'Iniciar sesion',
      submitting: 'Iniciando sesion...',
      supportCopy: 'Necesita acceso? Contacte al administrador de su organizacion.',
      privacyPolicy: 'Politica de privacidad',
      securityProtocol: 'Protocolo de seguridad',
      systemStatus: 'ACTIVE',
      heroLead: 'Una plataforma',
      heroMiddle: 'moderna de inteligencia',
      heroAccent: 'clinica.',
      genericError: 'No se pudo iniciar sesion.',
    },
  },
  admin: {
    en: {
      contextLabel: 'Elen1 Admin',
      panelKicker: 'System verification',
      headline: 'Welcome Back',
      subheadline: 'Enter your administrator credentials to access the control plane.',
      emailLabel: 'Email address',
      passwordLabel: 'Password',
      forgotPassword: 'Forgot password?',
      rememberDevice: 'Remember device for 30 days',
      submit: 'Sign in',
      submitting: 'Signing in...',
      supportCopy: 'Need access ? Contact your organization administrator.',
      privacyPolicy: 'Privacy policy',
      securityProtocol: 'Security protocol',
      systemStatus: 'ACTIVE',
      heroLead: 'A Modern',
      heroMiddle: 'Clinical Intelligence',
      heroAccent: 'Platform.',
      genericError: 'Unable to sign in.',
    },
    es: {
      contextLabel: 'Elen1 Admin',
      panelKicker: 'Verificacion del sistema',
      headline: 'Bienvenido de nuevo',
      subheadline: 'Ingrese sus credenciales de administrador para acceder al panel de control.',
      emailLabel: 'Correo electronico',
      passwordLabel: 'Contrasena',
      forgotPassword: 'Olvido su contrasena?',
      rememberDevice: 'Recordar este dispositivo durante 30 dias',
      submit: 'Iniciar sesion',
      submitting: 'Iniciando sesion...',
      supportCopy: 'Necesita acceso? Contacte al administrador de su organizacion.',
      privacyPolicy: 'Politica de privacidad',
      securityProtocol: 'Protocolo de seguridad',
      systemStatus: 'ACTIVE',
      heroLead: 'Una plataforma',
      heroMiddle: 'moderna de inteligencia',
      heroAccent: 'clinica.',
      genericError: 'No se pudo iniciar sesion.',
    },
  },
  portal: {
    en: {
      contextLabel: 'Elen1 Patient Portal',
      panelKicker: 'System verification',
      headline: 'Welcome Back',
      subheadline: 'Enter your patient portal credentials to continue.',
      emailLabel: 'Email address',
      passwordLabel: 'Password',
      forgotPassword: 'Forgot password?',
      rememberDevice: 'Remember device for 30 days',
      submit: 'Sign in',
      submitting: 'Signing in...',
      supportCopy: 'Need a reset or portal access? Contact your clinic directly.',
      privacyPolicy: 'Privacy policy',
      securityProtocol: 'Security protocol',
      systemStatus: 'ACTIVE',
      heroLead: 'A Modern',
      heroMiddle: 'Clinical Intelligence',
      heroAccent: 'Platform.',
      genericError: 'Unable to sign in.',
    },
    es: {
      contextLabel: 'Elen1 Patient Portal',
      panelKicker: 'Verificacion del sistema',
      headline: 'Bienvenido de nuevo',
      subheadline: 'Ingrese sus credenciales del portal del paciente para continuar.',
      emailLabel: 'Correo electronico',
      passwordLabel: 'Contrasena',
      forgotPassword: 'Olvido su contrasena?',
      rememberDevice: 'Recordar este dispositivo durante 30 dias',
      submit: 'Iniciar sesion',
      submitting: 'Iniciando sesion...',
      supportCopy: 'Necesita un restablecimiento o acceso al portal? Contacte directamente a su clinica.',
      privacyPolicy: 'Politica de privacidad',
      securityProtocol: 'Protocolo de seguridad',
      systemStatus: 'ACTIVE',
      heroLead: 'Una plataforma',
      heroMiddle: 'moderna de inteligencia',
      heroAccent: 'clinica.',
      genericError: 'No se pudo iniciar sesion.',
    },
  },
};

const PASSWORD_RESET_COPY: Record<LoginLanguage, PasswordResetCopy> = {
  en: {
    emailPlaceholder: 'doctor@elen1.com',
    passwordPlaceholder: '••••••••••••',
    forgotHeadline: 'Reset Password',
    forgotSubheadline: 'Enter your email address and we will send you a secure reset link.',
    forgotSubmit: 'Send reset link',
    forgotSubmitting: 'Sending reset link...',
    forgotSuccess: 'If that email is registered, a password reset link has been sent.',
    backToSignIn: 'Back to sign in',
    resetHeadline: 'Choose a New Password',
    resetSubheadline: 'Set a new password for your account using the secure link from your email.',
    newPasswordLabel: 'New password',
    confirmPasswordLabel: 'Confirm new password',
    newPasswordPlaceholder: 'Create a strong password',
    confirmPasswordPlaceholder: 'Repeat your new password',
    resetSubmit: 'Reset password',
    resetSubmitting: 'Resetting password...',
    resetSuccess: 'Your password has been reset. You can sign in now.',
    passwordGuidance: 'Password guidance: use at least 12 characters with uppercase, lowercase, a number, and a symbol.',
    passwordMismatch: 'New password and confirmation do not match.',
    resetTokenMissing: 'This reset link is incomplete. Request a new password reset email.',
    resetTokenInvalid: 'This password reset link is invalid or has expired.',
    samePasswordError: 'New password must be different from your current password.',
    requestNewLink: 'Request new link',
  },
  es: {
    emailPlaceholder: 'doctor@elen1.com',
    passwordPlaceholder: '••••••••••••',
    forgotHeadline: 'Restablecer contrasena',
    forgotSubheadline: 'Ingresa tu correo y te enviaremos un enlace seguro para restablecerla.',
    forgotSubmit: 'Enviar enlace',
    forgotSubmitting: 'Enviando enlace...',
    forgotSuccess: 'Si ese correo esta registrado, se envio un enlace para restablecer la contrasena.',
    backToSignIn: 'Volver al inicio de sesion',
    resetHeadline: 'Elige una nueva contrasena',
    resetSubheadline: 'Define una nueva contrasena usando el enlace seguro enviado a tu correo.',
    newPasswordLabel: 'Nueva contrasena',
    confirmPasswordLabel: 'Confirmar nueva contrasena',
    newPasswordPlaceholder: 'Crea una contrasena segura',
    confirmPasswordPlaceholder: 'Repite tu nueva contrasena',
    resetSubmit: 'Restablecer contrasena',
    resetSubmitting: 'Restableciendo contrasena...',
    resetSuccess: 'Tu contrasena fue restablecida. Ya puedes iniciar sesion.',
    passwordGuidance: 'Guia de contrasena: usa al menos 12 caracteres con mayusculas, minusculas, un numero y un simbolo.',
    passwordMismatch: 'La nueva contrasena y la confirmacion no coinciden.',
    resetTokenMissing: 'Este enlace de restablecimiento esta incompleto. Solicita un nuevo correo de restablecimiento.',
    resetTokenInvalid: 'Este enlace de restablecimiento no es valido o ya vencio.',
    samePasswordError: 'La nueva contrasena debe ser diferente de la actual.',
    requestNewLink: 'Solicitar nuevo enlace',
  },
};

function getLoginCopy(variant: AuthLoginVariant, language: LoginLanguage): LoginCopy {
  return {
    ...LOGIN_COPY_BASE[variant][language],
    ...PASSWORD_RESET_COPY[language],
  };
}

const AuthContext = createContext<AuthContextValue | null>(null);

function createLoginRequiredError() {
  const error = new Error('Login required') as Error & { error?: string };
  error.error = 'login_required';
  return error;
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  const parts = token.split('.');
  if (parts.length < 2) return {};
  try {
    const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
    return JSON.parse(window.atob(padded)) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function buildUserFromPayload(payload: Record<string, unknown>, currentUser?: AuthUser | null): AuthUser {
  const givenName = typeof payload.given_name === 'string' ? payload.given_name : currentUser?.given_name;
  const familyName = typeof payload.family_name === 'string' ? payload.family_name : currentUser?.family_name;
  const fullName =
    (typeof payload.name === 'string' && payload.name.trim()) ||
    [givenName, familyName].filter(Boolean).join(' ') ||
    currentUser?.name ||
    undefined;

  return {
    sub: typeof payload.sub === 'string' ? payload.sub : currentUser?.sub,
    email: typeof payload.email === 'string' ? payload.email : currentUser?.email,
    name: fullName,
    given_name: givenName,
    family_name: familyName,
    picture: typeof payload.picture === 'string' ? payload.picture : currentUser?.picture ?? null,
    role: typeof payload.role === 'string' ? payload.role : currentUser?.role,
    roles: Array.isArray(payload.roles)
      ? payload.roles.map((entry) => String(entry))
      : currentUser?.roles,
    platformAdmin: payload.platformAdmin === true || currentUser?.platformAdmin === true,
  };
}

function deriveExpiry(token: string) {
  const payload = decodeJwtPayload(token);
  const exp = typeof payload.exp === 'number' ? payload.exp : Number(payload.exp || 0);
  if (!Number.isFinite(exp) || exp <= 0) {
    return Date.now() + 15 * 60 * 1000;
  }
  return exp * 1000;
}

function loadStoredSession() {
  const raw = window.localStorage.getItem(STORAGE_KEY) || window.sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredSession;
    if (!parsed?.accessToken) return null;
    return parsed;
  } catch {
    return null;
  }
}

function clearStoredSession() {
  window.localStorage.removeItem(STORAGE_KEY);
  window.sessionStorage.removeItem(STORAGE_KEY);
}

function persistSession(session: StoredSession) {
  clearStoredSession();
  const serialized = JSON.stringify(session);
  if (session.remember) {
    window.localStorage.setItem(STORAGE_KEY, serialized);
    const raw = window.localStorage.getItem(CONNECTED_ACCOUNTS_KEY);
    let existing: StoredSession[] = [];
    try {
      existing = raw ? JSON.parse(raw) as StoredSession[] : [];
    } catch {
      existing = [];
    }
    const next = [
      session,
      ...existing.filter((item) => item.user?.email !== session.user?.email),
    ].slice(0, 6);
    window.localStorage.setItem(CONNECTED_ACCOUNTS_KEY, JSON.stringify(next));
  } else {
    window.sessionStorage.setItem(STORAGE_KEY, serialized);
  }
}

async function readResponseMessage(response: Response, fallback: string) {
  const text = await response.text().catch(() => '');
  if (!text.trim()) return fallback;
  try {
    const parsed = JSON.parse(text) as { message?: string; error?: string };
    return parsed.message || parsed.error || text;
  } catch {
    return text;
  }
}

function normalizeLoginLanguage(value?: unknown): LoginLanguage | null {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase();
  if (normalized.startsWith('es')) return 'es';
  if (normalized.startsWith('en')) return 'en';
  return null;
}

function inferLanguageFromPath(path?: string | null): LoginLanguage | null {
  if (typeof path !== 'string') return null;
  const normalized = path.trim().toLowerCase();
  if (!normalized) return null;
  if (/^\/(?:es|es-co|es-es|es-mx)(?:\/|$)/.test(normalized)) return 'es';
  if (/^\/en(?:\/|$)/.test(normalized)) return 'en';
  return null;
}

function resolveHostDefaultLanguage(): LoginLanguage | null {
  if (typeof window === 'undefined') return null;
  return HOST_DEFAULT_LANGUAGE_MAP[window.location.hostname] ?? null;
}

function resolveReferrerLanguage(): LoginLanguage | null {
  if (typeof document === 'undefined' || !document.referrer) return null;
  try {
    const referrer = new URL(document.referrer);
    return inferLanguageFromPath(referrer.pathname) || HOST_DEFAULT_LANGUAGE_MAP[referrer.hostname] || null;
  } catch {
    return null;
  }
}

function resolveCurrentLoginLanguage(path?: string | null): LoginLanguage {
  return (
    inferLanguageFromPath(path) ||
    resolveReferrerLanguage() ||
    resolveHostDefaultLanguage() ||
    (typeof document !== 'undefined' ? normalizeLoginLanguage(document.documentElement.lang) : null) ||
    (typeof navigator !== 'undefined' ? normalizeLoginLanguage(navigator.language) : null) ||
    'en'
  );
}

function resolveLoginLanguage(searchParams: URLSearchParams): LoginLanguage {
  return (
    normalizeLoginLanguage(
      searchParams.get(LOGIN_LANGUAGE_QUERY_PARAM) ||
        searchParams.get('lang') ||
        searchParams.get('locale'),
    ) ||
    inferLanguageFromPath(searchParams.get('returnTo')) ||
    resolveCurrentLoginLanguage()
  );
}

async function fetchCurrentUser(accessToken: string, currentUser?: AuthUser | null): Promise<AuthUser> {
  const payload = decodeJwtPayload(accessToken);
  if (payload.platformAdmin === true) {
    return buildUserFromPayload(payload, currentUser);
  }

  const response = await fetch(`${API_BASE}/auth/me`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    throw new Error(await readResponseMessage(response, 'Unable to load session.'));
  }

  const profile = (await response.json()) as {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    roles: string[];
  };

  return {
    sub: profile.id,
    email: profile.email,
    given_name: profile.firstName,
    family_name: profile.lastName,
    name: `${profile.firstName} ${profile.lastName}`.trim(),
    picture: null,
    role: profile.role,
    roles: profile.roles,
    platformAdmin: false,
  };
}

async function refreshSession(current: StoredSession): Promise<StoredSession> {
  if (!current.refreshToken) {
    throw createLoginRequiredError();
  }

  const response = await fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      refreshToken: current.refreshToken,
    }),
  });

  if (!response.ok) {
    throw createLoginRequiredError();
  }

  const payload = (await response.json()) as { accessToken: string };
  const user = await fetchCurrentUser(payload.accessToken, current.user);
  const nextSession: StoredSession = {
    ...current,
    accessToken: payload.accessToken,
    user,
    expiresAt: deriveExpiry(payload.accessToken),
  };
  persistSession(nextSession);
  return nextSession;
}

function buildLoginUrl(returnTo?: string, language?: LoginLanguage) {
  const params = new URLSearchParams();
  if (returnTo && returnTo.trim()) {
    params.set('returnTo', returnTo);
  }
  const resolvedLanguage = language || resolveCurrentLoginLanguage(returnTo);
  if (resolvedLanguage !== 'en') {
    params.set(LOGIN_LANGUAGE_QUERY_PARAM, resolvedLanguage);
  }
  const query = params.toString();
  return query ? `${LOGIN_PATH}?${query}` : LOGIN_PATH;
}

function normalizeAuthMode(value?: string | null, token?: string | null): AuthMode {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
  if (normalized === 'forgot') return 'forgot';
  if (normalized === 'reset' || (token && token.trim())) return 'reset';
  return 'login';
}

function validatePasswordPolicy(nextPassword: string) {
  const value = String(nextPassword || '');
  if (value.length < 12) return 'Password must be at least 12 characters long.';
  if (!/[A-Z]/.test(value)) return 'Password must include at least one uppercase letter.';
  if (!/[a-z]/.test(value)) return 'Password must include at least one lowercase letter.';
  if (!/[0-9]/.test(value)) return 'Password must include at least one number.';
  if (!/[^A-Za-z0-9]/.test(value)) return 'Password must include at least one symbol.';
  return null;
}

async function requestPasswordReset(email: string, language: LoginLanguage, fallback: string) {
  const response = await fetch(`${API_BASE}/auth/forgot-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, language }),
  });

  if (!response.ok) {
    throw new Error(await readResponseMessage(response, fallback));
  }
}

async function submitPasswordReset(token: string, newPassword: string, fallback: string) {
  const response = await fetch(`${API_BASE}/auth/reset-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token, newPassword }),
  });

  if (!response.ok) {
    throw new Error(await readResponseMessage(response, fallback));
  }
}

function mapResetErrorMessage(message: string, copy: LoginCopy) {
  if (message === 'Password reset link is invalid or expired.') return copy.resetTokenInvalid;
  if (message === 'New password must be different from current password.') return copy.samePasswordError;
  return message;
}

export function Auth0Provider({
  children,
}: {
  children: React.ReactNode;
  [key: string]: unknown;
}) {
  const [session, setSession] = useState<StoredSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const refreshPromiseRef = useRef<Promise<StoredSession> | null>(null);

  useEffect(() => {
    let active = true;
    const restore = async () => {
      const stored = loadStoredSession();
      if (!stored) {
        if (active) setIsLoading(false);
        return;
      }

      try {
        const stillValid = stored.expiresAt > Date.now() + TOKEN_REFRESH_SKEW_MS;
        const nextSession = stillValid ? stored : await refreshSession(stored);
        if (!active) return;
        setSession(nextSession);
        setError(null);
      } catch (restoreError) {
        clearStoredSession();
        const nextError =
          restoreError instanceof Error && (restoreError as Error & { error?: string }).error === 'login_required'
            ? null
            : restoreError instanceof Error
              ? restoreError
              : new Error('Unable to restore session.');
        if (!active) return;
        setSession(null);
        setError(nextError);
      } finally {
        if (active) setIsLoading(false);
      }
    };

    restore().catch(() => {
      if (!active) return;
      setIsLoading(false);
    });

    return () => {
      active = false;
    };
  }, []);

  const getAccessTokenSilently = async () => {
    if (!session) {
      throw createLoginRequiredError();
    }

    if (session.expiresAt > Date.now() + TOKEN_REFRESH_SKEW_MS) {
      return session.accessToken;
    }

    if (!refreshPromiseRef.current) {
      refreshPromiseRef.current = refreshSession(session).finally(() => {
        refreshPromiseRef.current = null;
      });
    }

    try {
      const nextSession = await refreshPromiseRef.current;
      setSession(nextSession);
      setError(null);
      return nextSession.accessToken;
    } catch {
      clearStoredSession();
      setSession(null);
      setError(null);
      throw createLoginRequiredError();
    }
  };

  const loginWithRedirect = async (options?: Record<string, unknown> & { appState?: { returnTo?: string } }) => {
    const returnTo =
      options?.appState?.returnTo ||
      `${window.location.pathname}${window.location.search}${window.location.hash}` ||
      DEFAULT_RETURN_TO;
    window.location.assign(buildLoginUrl(returnTo, resolveCurrentLoginLanguage(returnTo)));
  };

  const logout = (options?: { logoutParams?: { returnTo?: string } }) => {
    clearStoredSession();
    setSession(null);
    setError(null);
    const returnTo =
      options?.logoutParams?.returnTo ||
      buildLoginUrl(DEFAULT_RETURN_TO, resolveCurrentLoginLanguage(DEFAULT_RETURN_TO));
    window.location.assign(returnTo);
  };

  const loginWithPassword = async (
    email: string,
    password: string,
    options?: { remember?: boolean; language?: LoginLanguage },
  ) => {
    const response = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      throw new Error(await readResponseMessage(response, getLoginCopy('staff', options?.language ?? 'en').genericError));
    }

    const payload = (await response.json()) as {
      accessToken?: string;
      refreshToken?: string;
      mustChangePassword?: boolean;
      isPlatformAdmin?: boolean;
      requiresTwoFactor?: boolean;
      challengeId?: string;
      method?: string;
      destination?: string;
      expiresAt?: string;
    };

    if (payload.requiresTwoFactor) {
      return {
        requiresTwoFactor: true,
        challengeId: payload.challengeId,
        method: payload.method,
        destination: payload.destination,
        expiresAt: payload.expiresAt,
        mustChangePassword: payload.mustChangePassword,
        isPlatformAdmin: false,
      };
    }

    if (!payload.accessToken || !payload.refreshToken) {
      throw new Error(getLoginCopy('staff', options?.language ?? 'en').genericError);
    }

    const user = await fetchCurrentUser(payload.accessToken);
    const nextSession: StoredSession = {
      accessToken: payload.accessToken,
      refreshToken: payload.refreshToken,
      user,
      expiresAt: deriveExpiry(payload.accessToken),
      remember: options?.remember ?? true,
    };

    persistSession(nextSession);
    setSession(nextSession);
    setError(null);

    return {
      mustChangePassword: Boolean(payload.mustChangePassword),
      isPlatformAdmin: payload.isPlatformAdmin === true,
    };
  };

  const verifyTwoFactor = async (
    challengeId: string,
    code: string,
    options?: { remember?: boolean },
  ) => {
    const response = await fetch(`${API_BASE}/auth/verify-2fa`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ challengeId, code }),
    });
    if (!response.ok) {
      throw new Error(await readResponseMessage(response, 'Unable to verify the authentication code.'));
    }
    const payload = (await response.json()) as {
      accessToken: string;
      refreshToken: string;
      mustChangePassword: boolean;
      isPlatformAdmin?: boolean;
    };
    const user = await fetchCurrentUser(payload.accessToken);
    const nextSession: StoredSession = {
      accessToken: payload.accessToken,
      refreshToken: payload.refreshToken,
      user,
      expiresAt: deriveExpiry(payload.accessToken),
      remember: options?.remember ?? true,
    };
    persistSession(nextSession);
    setSession(nextSession);
    setError(null);
    return {
      mustChangePassword: payload.mustChangePassword,
      isPlatformAdmin: payload.isPlatformAdmin === true,
    };
  };

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: Boolean(session?.accessToken),
      isLoading,
      error,
      user: session?.user,
      getAccessTokenSilently,
      loginWithRedirect,
      logout,
      loginWithPassword,
      verifyTwoFactor,
    }),
    [error, isLoading, session],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth0() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth0 must be used within Auth0Provider.');
  }
  return context;
}

function HelpIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M9.7 9.2a2.54 2.54 0 0 1 4.98.78c0 1.72-1.74 2.14-2.44 3.06"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.8"
      />
      <circle cx="12" cy="16.85" r="1.05" fill="currentColor" />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="m12 3.3 1.42 1.08 1.82-.28.92 1.58 1.74.64-.1 1.79 1.19 1.34-1.02 1.5 1.02 1.5-1.19 1.34.1 1.79-1.74.64-.92 1.58-1.82-.28L12 20.7l-1.42-1.08-1.82.28-.92-1.58-1.74-.64.1-1.79-1.19-1.34 1.02-1.5-1.02-1.5 1.19-1.34-.1-1.79 1.74-.64.92-1.58 1.82.28L12 3.3Z"
        fill="none"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="1.35"
      />
      <circle cx="12" cy="12" r="2.7" fill="none" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

type AuthLoginPageProps = {
  variant?: AuthLoginVariant;
  contextLabel?: string;
  headline?: string;
  subheadline?: string;
  supportCopy?: string;
};

export function AuthLoginPage({
  variant = 'staff',
  contextLabel,
  headline,
  subheadline,
  supportCopy,
}: AuthLoginPageProps) {
  const { isAuthenticated, loginWithPassword, verifyTwoFactor } = useAuth0();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [twoFactorChallenge, setTwoFactorChallenge] = useState<{
    id: string;
    method?: string;
    destination?: string;
    expiresAt?: string;
  } | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const returnTo = searchParams.get('returnTo') || DEFAULT_RETURN_TO;
  const resetToken = searchParams.get('token') || '';
  const authMode = normalizeAuthMode(searchParams.get('mode'), resetToken);
  const loginLanguage = useMemo(() => resolveLoginLanguage(searchParams), [searchParams]);
  const copy = useMemo(() => getLoginCopy(variant, loginLanguage), [loginLanguage, variant]);
  const resolvedContextLabel = contextLabel ?? copy.contextLabel;
  const resolvedHeadline =
    authMode === 'forgot'
      ? copy.forgotHeadline
      : authMode === 'reset'
        ? copy.resetHeadline
        : twoFactorChallenge
          ? 'Enter authentication code'
          : headline ?? copy.headline;
  const resolvedSubheadline =
    authMode === 'forgot'
      ? copy.forgotSubheadline
      : authMode === 'reset'
        ? copy.resetSubheadline
        : twoFactorChallenge
          ? `We sent a secure login code${twoFactorChallenge.destination ? ` to ${twoFactorChallenge.destination}` : ''}.`
          : subheadline ?? copy.subheadline;
  const resolvedSupportCopy = supportCopy ?? copy.supportCopy;
  const effectiveError = error || (authMode === 'reset' && !resetToken.trim() ? copy.resetTokenMissing : null);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(returnTo, { replace: true });
    }
  }, [isAuthenticated, navigate, returnTo]);

  useEffect(() => {
    document.documentElement.setAttribute('lang', loginLanguage);
  }, [loginLanguage]);

  useEffect(() => {
    setError(null);
    setSuccess(null);
    setTwoFactorChallenge(null);
    setTwoFactorCode('');
    if (authMode !== 'login') {
      setPassword('');
      setRemember(true);
    }
    if (authMode !== 'reset') {
      setNewPassword('');
      setConfirmPassword('');
    }
  }, [authMode]);

  const updateMode = (mode: AuthMode) => {
    const next = new URLSearchParams(searchParams);
    if (mode === 'login') {
      next.delete('mode');
      next.delete('token');
    } else {
      next.set('mode', mode);
      if (mode !== 'reset') {
        next.delete('token');
      }
    }
    setSearchParams(next, { replace: true });
  };

  const resendTwoFactorCode = async () => {
    if (!twoFactorChallenge) return;
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await fetch(`${API_BASE}/auth/2fa/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challengeId: twoFactorChallenge.id }),
      });
      if (!response.ok) {
        throw new Error(await readResponseMessage(response, 'Unable to send a new authentication code.'));
      }
      const payload = await response.json() as { challengeId: string; method?: string; destination?: string; expiresAt?: string };
      setTwoFactorChallenge({
        id: payload.challengeId,
        method: payload.method,
        destination: payload.destination,
        expiresAt: payload.expiresAt,
      });
      setTwoFactorCode('');
      setSuccess('New authentication code sent.');
    } catch (resendError) {
      setError(resendError instanceof Error ? resendError.message : copy.genericError);
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      if (authMode === 'forgot') {
        await requestPasswordReset(email, loginLanguage, copy.genericError);
        setSuccess(copy.forgotSuccess);
      } else if (authMode === 'reset') {
        if (!resetToken.trim()) {
          setError(copy.resetTokenMissing);
          return;
        }
        if (newPassword !== confirmPassword) {
          setError(copy.passwordMismatch);
          return;
        }
        const policyError = validatePasswordPolicy(newPassword);
        if (policyError) {
          setError(policyError);
          return;
        }
        await submitPasswordReset(resetToken, newPassword, copy.genericError);
        setSuccess(copy.resetSuccess);
        setNewPassword('');
        setConfirmPassword('');
      } else if (twoFactorChallenge) {
        await verifyTwoFactor(twoFactorChallenge.id, twoFactorCode, { remember });
        navigate(returnTo, { replace: true });
      } else {
        const result = await loginWithPassword(email, password, { remember, language: loginLanguage });
        if (result.requiresTwoFactor && result.challengeId) {
          setTwoFactorChallenge({
            id: result.challengeId,
            method: result.method,
            destination: result.destination,
            expiresAt: result.expiresAt,
          });
          setTwoFactorCode('');
          setSuccess('Authentication code sent.');
          return;
        }
        navigate(returnTo, { replace: true });
      }
    } catch (submitError) {
      const message = submitError instanceof Error ? submitError.message : copy.genericError;
      setError(authMode === 'reset' ? mapResetErrorMessage(message, copy) : message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-topbar">
          <div className="auth-wordmark" title={resolvedContextLabel}>
            elen1
          </div>
          <div className="auth-tools" aria-hidden="true">
            <span className="auth-tool">
              <HelpIcon />
            </span>
            <span className="auth-tool">
              <SettingsIcon />
            </span>
          </div>
        </div>

        <section className="auth-hero">
          <div className="auth-hero-veil auth-hero-veil-one" />
          <div className="auth-hero-veil auth-hero-veil-two" />

          <div className="auth-hero-copy">
            <h1>
              {copy.heroLead}
              <br />
              {copy.heroMiddle}
              <br />
              <span>{copy.heroAccent}</span>
            </h1>
          </div>

          <p className="auth-hero-footer">© 2026 ELEN1 SYSTEMS. ALL RIGHTS RESERVED.</p>
        </section>

        <section className="auth-panel">
          <div className="auth-form-shell" data-context-label={resolvedContextLabel}>
            <p className="auth-panel-kicker">{copy.panelKicker}</p>
            <h2 className="auth-panel-title">{resolvedHeadline}</h2>
            <p className="auth-panel-copy">{resolvedSubheadline}</p>

            <form className="auth-form" onSubmit={onSubmit}>
              {authMode === 'reset' ? (
                <>
                  <label className="auth-field">
                    <span className="auth-field-label">{copy.newPasswordLabel}</span>
                    <input
                      type="password"
                      autoComplete="new-password"
                      value={newPassword}
                      onChange={(event) => setNewPassword(event.target.value)}
                      placeholder={copy.newPasswordPlaceholder}
                      required
                    />
                  </label>

                  <label className="auth-field">
                    <span className="auth-field-label">{copy.confirmPasswordLabel}</span>
                    <input
                      type="password"
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(event) => setConfirmPassword(event.target.value)}
                      placeholder={copy.confirmPasswordPlaceholder}
                      required
                    />
                  </label>

                  <p className="auth-helper">{copy.passwordGuidance}</p>
                </>
              ) : twoFactorChallenge ? (
                <>
                  <label className="auth-field">
                    <span className="auth-field-label">Authentication code</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={twoFactorCode}
                      onChange={(event) => setTwoFactorCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      required
                    />
                  </label>
                  <p className="auth-helper">
                    {twoFactorChallenge.expiresAt ? `Code expires at ${new Date(twoFactorChallenge.expiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}. ` : ''}
                    Missing the passcode? Request a new code or contact your administrator for lockout recovery.
                  </p>
                </>
              ) : (
                <>
                  <label className="auth-field">
                    <span className="auth-field-label">{copy.emailLabel}</span>
                    <input
                      type="email"
                      autoComplete="username"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      placeholder={copy.emailPlaceholder}
                      required
                    />
                  </label>

                  {authMode === 'login' ? (
                    <>
                      <label className="auth-field">
                        <span className="auth-field-heading">
                          <span className="auth-field-label">{copy.passwordLabel}</span>
                          <button
                            type="button"
                            className="auth-link-button"
                            onClick={() => updateMode('forgot')}
                          >
                            {copy.forgotPassword}
                          </button>
                        </span>
                        <input
                          type="password"
                          autoComplete="current-password"
                          value={password}
                          onChange={(event) => setPassword(event.target.value)}
                          placeholder={copy.passwordPlaceholder}
                          required
                        />
                      </label>

                      <label className="auth-checkbox">
                        <input
                          type="checkbox"
                          checked={remember}
                          onChange={() => setRemember((current) => !current)}
                        />
                        <span className="auth-checkbox-box" />
                        <span>{copy.rememberDevice}</span>
                      </label>
                    </>
                  ) : null}
                </>
              )}

              {effectiveError ? <div className="auth-error">{effectiveError}</div> : null}
              {success ? <div className="auth-success">{success}</div> : null}

              {!(authMode === 'reset' && Boolean(success)) ? (
                <button type="submit" className="auth-submit" disabled={submitting}>
                  {submitting
                    ? authMode === 'forgot'
                      ? copy.forgotSubmitting
                      : authMode === 'reset'
                        ? copy.resetSubmitting
                        : copy.submitting
                    : authMode === 'forgot'
                      ? copy.forgotSubmit
                      : authMode === 'reset'
                        ? copy.resetSubmit
                        : twoFactorChallenge
                          ? 'Verify code'
                          : copy.submit}
                </button>
              ) : null}

              {twoFactorChallenge ? (
                <>
                  <button
                    type="button"
                    className="auth-secondary-action"
                    onClick={() => void resendTwoFactorCode()}
                    disabled={submitting}
                  >
                    Resend code
                  </button>
                  <button
                    type="button"
                    className="auth-secondary-action"
                    onClick={() => {
                      setTwoFactorChallenge(null);
                      setTwoFactorCode('');
                      setSuccess(null);
                    }}
                  >
                    Back to password
                  </button>
                </>
              ) : null}

              {authMode !== 'login' && !twoFactorChallenge ? (
                <button
                  type="button"
                  className="auth-secondary-action"
                  onClick={() => updateMode('login')}
                >
                  {copy.backToSignIn}
                </button>
              ) : null}

              {authMode === 'reset' && !resetToken.trim() ? (
                <button
                  type="button"
                  className="auth-secondary-action"
                  onClick={() => updateMode('forgot')}
                >
                  {copy.requestNewLink}
                </button>
              ) : null}
            </form>

            <div className="auth-panel-divider" />
            <p className="auth-panel-support">{resolvedSupportCopy}</p>
          </div>

          <div className="auth-panel-footer">
            <span>{copy.privacyPolicy}</span>
            <span>{copy.securityProtocol}</span>
            <span className="auth-system-status">
              <span className="auth-system-status-dot" />
              {copy.systemStatus}
            </span>
          </div>
        </section>
      </div>
    </div>
  );
}
